import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  BookOpen, 
  BarChart3, 
  Settings,
  Search,
  ChevronRight,
  Check,
  X,
  LogOut,
  Shield,
  Zap,
  Eye,
  RotateCcw,
  AlertTriangle,
  Trash2,
  GraduationCap,
  UserCog,
  Crown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { useAuth } from '../context/AuthContext';
import { cn } from '../lib/utils';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role definitions
const ROLES = {
  student: { name: "Aluno", icon: Users, color: "text-blue-500" },
  teacher: { name: "Professor", icon: GraduationCap, color: "text-green-500" },
  admin: { name: "Administrador", icon: Crown, color: "text-yellow-500" },
};

// Module definitions
const MODULES = {
  "daily-life-1": { name: "Daily Life - Básico", free: true },
  "daily-life-2": { name: "Daily Life - Intermediário", free: false },
  "daily-life-3": { name: "Daily Life - Avançado", free: false },
  "work-1": { name: "Work & Factory - Básico", free: false },
  "work-2": { name: "Work & Factory - Intermediário", free: false },
  "truck-1": { name: "Truck & Logistics - Básico", free: false },
  "truck-2": { name: "Truck & Logistics - Avançado", free: false },
  "business-1": { name: "Business - Básico", free: false },
  "business-2": { name: "Business - Avançado", free: false },
};

const AdminPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/stats`, { withCredentials: true })
      ]);
      setUsers(usersRes.data.users);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGrantModule = async (userId, moduleId) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${userId}/grant-module`,
        { module_id: moduleId },
        { withCredentials: true }
      );
      await fetchData();
      // Update selected user
      if (selectedUser?.user_id === userId) {
        const updatedUser = users.find(u => u.user_id === userId);
        if (updatedUser) setSelectedUser(updatedUser);
      }
    } catch (error) {
      console.error('Error granting module:', error);
      alert('Erro ao liberar módulo');
    }
  };

  const handleRevokeModule = async (userId, moduleId) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${userId}/revoke-module`,
        { module_id: moduleId },
        { withCredentials: true }
      );
      await fetchData();
    } catch (error) {
      console.error('Error revoking module:', error);
      alert('Erro ao revogar módulo');
    }
  };

  const handleResetProgress = async (userId, userName) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${userId}/reset-progress`,
        {},
        { withCredentials: true }
      );
      await fetchData();
      // Update selected user with reset values
      if (selectedUser?.user_id === userId) {
        setSelectedUser(prev => ({
          ...prev,
          total_xp: 0,
          level: 1,
          current_streak: 0,
          lessons_completed: []
        }));
      }
      alert(`Progresso de ${userName} foi zerado com sucesso!`);
    } catch (error) {
      console.error('Error resetting progress:', error);
      alert('Erro ao zerar progresso');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    try {
      await axios.delete(
        `${API_URL}/api/admin/users/${userId}`,
        { withCredentials: true }
      );
      await fetchData();
      alert(`Usuário ${userName} foi deletado com sucesso!`);
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(error.response?.data?.detail || 'Erro ao deletar usuário');
    }
  };

  const handleSetRole = async (userId, userName, newRole) => {
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${userId}/set-role`,
        { role: newRole },
        { withCredentials: true }
      );
      await fetchData();
      const roleName = ROLES[newRole]?.name || newRole;
      alert(`${userName} agora é ${roleName}!`);
    } catch (error) {
      console.error('Error setting role:', error);
      alert(error.response?.data?.detail || 'Erro ao alterar role');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Check if current user is admin (not just teacher)
  const isAdmin = user?.is_admin || user?.role === 'admin';

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
              <Shield className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="font-semibold">Painel Admin</h1>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/')}>
              Ver App
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="card-elevated p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
                <p className="text-xs text-muted-foreground">Alunos</p>
              </div>
            </div>
          </div>
          
          <div className="card-elevated p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_lessons_completed || 0}</p>
                <p className="text-xs text-muted-foreground">Lições Feitas</p>
              </div>
            </div>
          </div>
          
          <div className="card-elevated p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                <Zap className="w-5 h-5 text-accent" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.modules?.["daily-life-2"] || 0}</p>
                <p className="text-xs text-muted-foreground">Módulos Pagos</p>
              </div>
            </div>
          </div>
          
          <div className="card-elevated p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-warning/10 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold">9</p>
                <p className="text-xs text-muted-foreground">Módulos Total</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 border-b border-border pb-2">
          <button
            onClick={() => setActiveTab('users')}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              activeTab === 'users' 
                ? 'bg-primary text-primary-foreground' 
                : 'text-muted-foreground hover:bg-muted'
            )}
          >
            <Users className="w-4 h-4 inline mr-2" />
            Alunos
          </button>
          <button
            onClick={() => setActiveTab('modules')}
            className={cn(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              activeTab === 'modules' 
                ? 'bg-primary text-primary-foreground' 
                : 'text-muted-foreground hover:bg-muted'
            )}
          >
            <BookOpen className="w-4 h-4 inline mr-2" />
            Módulos
          </button>
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome ou email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Users List */}
            <div className="card-elevated overflow-hidden">
              <div className="grid grid-cols-12 gap-4 p-3 bg-muted/50 text-xs font-medium text-muted-foreground border-b border-border">
                <div className="col-span-3">Usuário</div>
                <div className="col-span-2 text-center">Role</div>
                <div className="col-span-2 text-center">Nível/XP</div>
                <div className="col-span-2 text-center">Módulos</div>
                <div className="col-span-3 text-center">Ações</div>
              </div>
              
              {filteredUsers.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  Nenhum usuário encontrado
                </div>
              ) : (
                filteredUsers.map((u) => {
                  const userRole = u.role || (u.is_admin ? 'admin' : 'student');
                  const RoleIcon = ROLES[userRole]?.icon || Users;
                  const roleColor = ROLES[userRole]?.color || 'text-muted-foreground';
                  
                  return (
                  <div
                    key={u.user_id}
                    className="grid grid-cols-12 gap-4 p-3 items-center border-b border-border last:border-0 hover:bg-muted/30"
                  >
                    <div className="col-span-3">
                      <div className="flex items-center gap-3">
                        {u.picture ? (
                          <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-xs font-medium">{u.name?.[0]}</span>
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{u.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                        </div>
                      </div>
                    </div>
                    <div className="col-span-2 text-center">
                      <span className={cn("flex items-center justify-center gap-1 text-xs font-medium", roleColor)}>
                        <RoleIcon className="w-3.5 h-3.5" />
                        {ROLES[userRole]?.name || 'Aluno'}
                      </span>
                    </div>
                    <div className="col-span-2 text-center">
                      <span className="text-xs">Nv.{u.level || 1}</span>
                      <span className="text-muted-foreground mx-1">•</span>
                      <span className="text-xs font-medium">{u.total_xp || 0} XP</span>
                    </div>
                    <div className="col-span-2 text-center">
                      <span className="text-sm font-medium">
                        {u.unlocked_modules?.length || 1}/{Object.keys(MODULES).length}
                      </span>
                    </div>
                    <div className="col-span-3 text-center flex gap-1 justify-center">
                      <Dialog open={dialogOpen && selectedUser?.user_id === u.user_id} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedUser(u)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle className="flex items-center gap-3">
                              {u.picture && (
                                <img src={u.picture} alt="" className="w-10 h-10 rounded-full" />
                              )}
                              <div>
                                <div className="flex items-center gap-2">
                                  <p>{u.name}</p>
                                  <span className={cn("flex items-center gap-1 text-xs", roleColor)}>
                                    <RoleIcon className="w-3 h-3" />
                                    {ROLES[userRole]?.name}
                                  </span>
                                </div>
                                <p className="text-sm font-normal text-muted-foreground">{u.email}</p>
                              </div>
                            </DialogTitle>
                          </DialogHeader>
                          
                          <div className="space-y-4">
                            {/* User Stats */}
                            <div className="grid grid-cols-3 gap-2 text-center">
                              <div className="p-2 rounded-lg bg-muted">
                                <p className="text-lg font-bold">{u.level || 1}</p>
                                <p className="text-xs text-muted-foreground">Nível</p>
                              </div>
                              <div className="p-2 rounded-lg bg-muted">
                                <p className="text-lg font-bold">{u.total_xp || 0}</p>
                                <p className="text-xs text-muted-foreground">XP</p>
                              </div>
                              <div className="p-2 rounded-lg bg-muted">
                                <p className="text-lg font-bold">{u.lessons_completed?.length || 0}</p>
                                <p className="text-xs text-muted-foreground">Lições</p>
                              </div>
                            </div>

                            {/* Role Management - Admin Only */}
                            {isAdmin && userRole !== 'admin' && (
                              <div className="space-y-2">
                                <p className="text-sm font-medium">Alterar Função:</p>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant={userRole === 'student' ? 'default' : 'outline'}
                                    className="flex-1"
                                    onClick={() => handleSetRole(u.user_id, u.name, 'student')}
                                    disabled={userRole === 'student'}
                                  >
                                    <Users className="w-4 h-4 mr-1" />
                                    Aluno
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant={userRole === 'teacher' ? 'default' : 'outline'}
                                    className="flex-1"
                                    onClick={() => handleSetRole(u.user_id, u.name, 'teacher')}
                                    disabled={userRole === 'teacher'}
                                  >
                                    <GraduationCap className="w-4 h-4 mr-1" />
                                    Professor
                                  </Button>
                                </div>
                              </div>
                            )}

                            {/* Reset Progress Button - Admin Only */}
                            {isAdmin && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button 
                                  variant="outline" 
                                  className="w-full border-warning/50 text-warning hover:bg-warning/10"
                                >
                                  <RotateCcw className="w-4 h-4 mr-2" />
                                  Zerar Progresso
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="flex items-center gap-2">
                                    <AlertTriangle className="w-5 h-5 text-warning" />
                                    Zerar Progresso
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Tem certeza que deseja zerar todo o progresso de <strong>{u.name}</strong>?
                                    <br /><br />
                                    Isso irá resetar:
                                    <ul className="list-disc list-inside mt-2 text-left">
                                      <li>XP total (voltará para 0)</li>
                                      <li>Nível (voltará para 1)</li>
                                      <li>Streak de dias</li>
                                      <li>Lições completadas</li>
                                    </ul>
                                    <br />
                                    <strong>Esta ação não pode ser desfeita!</strong>
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-warning text-warning-foreground hover:bg-warning/90"
                                    onClick={() => handleResetProgress(u.user_id, u.name)}
                                  >
                                    Zerar Progresso
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                            )}

                            {/* Modules - Admin Only can modify */}
                            <div>
                              <p className="text-sm font-medium mb-2">Módulos:</p>
                              <div className="space-y-2 max-h-40 overflow-y-auto">
                                {Object.entries(MODULES).map(([moduleId, moduleInfo]) => {
                                  const hasAccess = u.unlocked_modules?.includes(moduleId);
                                  return (
                                    <div
                                      key={moduleId}
                                      className={cn(
                                        'flex items-center justify-between p-2 rounded-lg',
                                        hasAccess ? 'bg-success/10' : 'bg-muted'
                                      )}
                                    >
                                      <div className="flex items-center gap-2">
                                        {hasAccess ? (
                                          <Check className="w-4 h-4 text-success" />
                                        ) : (
                                          <X className="w-4 h-4 text-muted-foreground" />
                                        )}
                                        <div>
                                          <p className="text-sm font-medium">{moduleInfo.name}</p>
                                          {moduleInfo.free && (
                                            <span className="text-xs text-success">Grátis</span>
                                          )}
                                        </div>
                                      </div>
                                      {isAdmin && !moduleInfo.free && (
                                        <Button
                                          size="sm"
                                          variant={hasAccess ? "outline" : "default"}
                                          onClick={() => hasAccess 
                                            ? handleRevokeModule(u.user_id, moduleId)
                                            : handleGrantModule(u.user_id, moduleId)
                                          }
                                        >
                                          {hasAccess ? 'Revogar' : 'Liberar'}
                                        </Button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Delete User Button - Admin Only */}
                            {isAdmin && userRole !== 'admin' && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button 
                                  variant="outline" 
                                  className="w-full border-destructive/50 text-destructive hover:bg-destructive/10"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Deletar Usuário
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="flex items-center gap-2">
                                    <Trash2 className="w-5 h-5 text-destructive" />
                                    Deletar Usuário
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Tem certeza que deseja <strong>DELETAR PERMANENTEMENTE</strong> o usuário <strong>{u.name}</strong>?
                                    <br /><br />
                                    Todos os dados serão perdidos:
                                    <ul className="list-disc list-inside mt-2 text-left">
                                      <li>Perfil e informações pessoais</li>
                                      <li>Progresso e XP</li>
                                      <li>Módulos desbloqueados</li>
                                      <li>Histórico de lições</li>
                                    </ul>
                                    <br />
                                    <strong className="text-destructive">Esta ação NÃO pode ser desfeita!</strong>
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    onClick={() => {
                                      handleDeleteUser(u.user_id, u.name);
                                      setDialogOpen(false);
                                    }}
                                  >
                                    Deletar Permanentemente
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                            )}
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Modules Tab */}
        {activeTab === 'modules' && (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {Object.entries(MODULES).map(([moduleId, moduleInfo]) => (
                <div key={moduleId} className="card-elevated p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold">{moduleInfo.name}</h3>
                      {moduleInfo.free ? (
                        <span className="text-xs text-success font-medium">Módulo Gratuito</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">Módulo Pago</span>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">{stats?.modules?.[moduleId] || 0}</p>
                      <p className="text-xs text-muted-foreground">alunos</p>
                    </div>
                  </div>
                  <Progress 
                    value={((stats?.modules?.[moduleId] || 0) / Math.max(stats?.total_users || 1, 1)) * 100} 
                    className="h-2"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
